import { useEffect } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import type { GetDevicesQuery, GetDevicesQueryVariables } from "../../../API";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { GET_DEVICES } from "../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../common/variables/common";
import { errorNotification } from "../../../common/variables/notification";
import {
  useSelectedLocationDropdown,
  useSelectedTagIdDropdown,
} from "../../../common/variables/selectedLocationDropdown";

export const useGetDevicesCached = () => {
  const locationVariable = useSelectedLocationDropdown();
  const locationId = locationVariable?.value;
  const selectedCustomerId = useCustomerIdGuard();
  const locationTagVariable = useSelectedTagIdDropdown();

  const [getDevices, { data, fetchMore, loading, networkStatus, refetch }] =
    useLazyQuery<GetDevicesQuery, GetDevicesQueryVariables>(GET_DEVICES, {
      variables: {
        limit: DEFAULT_LIMIT_COUNT,
        customerId: selectedCustomerId,
        locationId,
      },
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    const notSelectedLocation = !locationId && !locationTagVariable;

    if (
      (!selectedCustomerId && notSelectedLocation) ||
      (selectedCustomerId && notSelectedLocation)
    )
      return;

    getDevices({
      variables: {
        limit: DEFAULT_LIMIT_COUNT,
        customerId: selectedCustomerId,
        locationId,
      },
    });
  }, [selectedCustomerId, locationId, locationTagVariable, getDevices]);

  useEffect(() => {
    if (!data?.getDevices?.nextToken || locationTagVariable) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getDevices.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: DEFAULT_LIMIT_COUNT,
              customerId: selectedCustomerId,
              locationId,
              nextToken: currentNextToken,
            },
          });

          currentNextToken = response.data?.getDevices?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional device pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [
    data?.getDevices?.nextToken,
    selectedCustomerId,
    locationId,
    locationTagVariable,
    fetchMore,
  ]);

  const devicesLoading =
    loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  return {
    data,
    loading: devicesLoading,
    refetch,
  };
};
